import { TypeManagerDecorator } from "../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, Input, signal } from "@angular/core";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25Util } from "../../util/s25-util";
import { EventService } from "../../services/event.service";
import { S25ObjectDetailsService } from "../object-details/s25.object.details.service";
import { Bind } from "../../decorators/bind.decorator";
import { S25Const } from "../../util/s25-const";

@TypeManagerDecorator("s25-ng-edit-ownership")
@Component({
    selector: "s25-ng-edit-ownership",
    template: `
        @if (hasContactPerms) {
            @if (noOwnerChange()) {
                <div class="cn-alert cn-alert--warning">This contact is currently the event owner</div>
            }
            <div class="assign-wrapper">
                <s25-contact-dropdown [(chosen)]="eventOwner" [r25UserOnly]="true"></s25-contact-dropdown>
                <s25-ng-button [type]="'primary'" [disabled]="assignBtnDisabled()" [onClick]="setOwner"
                    >Assign Owner</s25-ng-button
                >
            </div>
            <p class="form-divider">or</p>
        } @else {
            <div class="cn-alert cn-alert--warning">
                Insufficient permission to search for contacts. The minimum permission needed is 7.0 View Contacts under
                Basic Options in the Group Administration Tool.
            </div>
        }
        <s25-ng-button [type]="'primary'" [onClick]="takeOwnership">Take Ownership</s25-ng-button>
        @if (errorMsg()) {
            <p class="ngRed">{{ errorMsg() }}</p>
        }
        <s25-ng-fading-success-msg [message]="successMsg"></s25-ng-fading-success-msg>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 1em;
            gap: 1em;

            .cn-alert.cn-alert--warning {
                width: min(35em, 100%);
            }

            .assign-wrapper {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                gap: 1em;
                width: 100%;

                s25-contact-dropdown {
                    width: min(20em, 100%);
                }
            }

            .form-divider {
                display: flex;
                font-size: 1.05em;
                font-weight: 600;
                align-items: center;
                gap: 0.5em;

                &::before,
                &::after {
                    content: "";
                    width: 16em;
                    border-top: 1px solid #d3d3d3;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditOwnershipComponent {
    @Input() eventId: number;
    @Input() hasContactPerms: boolean;

    eventOwner = signal<DropDownItem>(null);
    errorMsg = signal("");
    successMsg = signal("");

    noOwnerChange = computed(() => this.eventOwner()?.itemId === this.objectDetailsService.eventOwner()?.itemId);
    assignBtnDisabled = computed(() => !this.eventOwner() || this.noOwnerChange());

    constructor(private objectDetailsService: S25ObjectDetailsService) {}

    @Bind
    async setOwner() {
        if (this.assignBtnDisabled()) return;
        const [_, err] = await S25Util.Maybe(EventService.setOwnership(this.eventId, +this.eventOwner().itemId));
        if (err) {
            this.errorMsg.set("An error occurred while setting the event owner. Please try again later.");
            return console.error(err);
        }
        this.errorMsg() && this.errorMsg.set("");
        this.successMsg.set("Event owner successfully updated");
        const { itemName, itemId } = this.eventOwner();
        this.objectDetailsService.setEventOwner({ itemName, itemId });
        this.eventOwner.set(null);
    }

    @Bind
    async takeOwnership() {
        const [user, err] = await S25Util.Maybe(EventService.takeOwnership(this.eventId));
        if (err) {
            this.errorMsg.set("An error occurred. Please try again later.");
            return console.error(err);
        }
        this.errorMsg() && this.errorMsg.set("");
        this.successMsg.set("You are now the event owner");
        user?.owner_cont_id &&
            this.objectDetailsService.setEventOwner({
                itemId: user.owner_cont_id,
                itemName: user.owner_name ?? S25Const.private,
            });
    }
}
